import { useCallback, useEffect, useMemo } from 'react';
import useNonNullContext from '../../../../../../Hooks/useNonNullContext';
import { CollectionsContext } from './CollectionsContext';
import { OrderByParam } from '../../../../../../Hooks/useReactTableSortToOrderBy';
import {
    getTiktokAudioCollectionPostStats,
    GetTiktokAudioCollectionPostStatsParams,
    TiktokAudioCollectionPostStats,
} from '@round/api';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';

type CollectionTopPostsValues = {
    rows: TiktokAudioCollectionPostStats[];
    count: number;
    loading: boolean;
    errorLoading: boolean;
    page: number;
    pageSize: number;
    search: string;
    orderBy: OrderByParam<TiktokAudioCollectionPostStats>;
};

type CollectionTopPostsUtils = {
    setPage: (page: number) => void;
    setPageSize: (pageSize: number | undefined) => void;
    setSearch: (query: string) => void;
    setOrderBy: (orderBy: OrderByParam<TiktokAudioCollectionPostStats>) => void;
    refresh: (collectionId: number) => Promise<void>;
};

const useCollectionTopPosts = (): [CollectionTopPostsValues, CollectionTopPostsUtils] => {
    const [
        {
            selectedCollection,
            topPostsStats,
            errorLoadingTopPostsStats,
            topPostsStatsLoading,
            topPostsStatsCount,
            urlState,
        },
        { setUrlState, dispatch },
    ] = useNonNullContext(CollectionsContext);

    const page = Number(urlState.vP ?? 1);
    const pageSize = Number(urlState.vPS ?? 10);
    const search = urlState.vS ?? '';
    const orderBy = useMemo(
        () => ({
            order_by_desc: urlState.vOD as keyof TiktokAudioCollectionPostStats,
            order_by_asc: urlState.vOA as keyof TiktokAudioCollectionPostStats,
        }),
        [urlState.vOA, urlState.vOD]
    );

    const fetchTopPostsStats = useCallback(
        async (collectionId: number, options: GetTiktokAudioCollectionPostStatsParams, requestInit?: RequestInit) => {
            try {
                dispatch({ type: 'loadTopPosts' });
                const response = await getTiktokAudioCollectionPostStats(collectionId, options, requestInit);
                if (response.status === 200) {
                    dispatch({ type: 'loadedTopPosts', payload: response.data });
                }
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                dispatch({ type: 'errorLoadingTopPosts' });
            }
        },
        [dispatch]
    );

    const refresh = useCallback(
        async (collectionId: number) => {
            await fetchTopPostsStats(collectionId, { ...orderBy, search, page, page_size: pageSize });
        },
        [fetchTopPostsStats, orderBy, page, pageSize, search]
    );

    useEffect(() => {
        if (selectedCollection?.id) {
            dispatch({ type: 'resetTopPosts' });
        }
    }, [dispatch, selectedCollection?.id]);

    useAbortableEffect(
        (signal) => {
            if (selectedCollection?.id) {
                fetchTopPostsStats(
                    selectedCollection.id,
                    { ...orderBy, search, page, page_size: pageSize },
                    { signal }
                );
            }
        },
        [fetchTopPostsStats, orderBy, page, pageSize, search, selectedCollection?.id]
    );

    const values: CollectionTopPostsValues = useMemo(
        () => ({
            rows: topPostsStats,
            count: topPostsStatsCount,
            errorLoading: errorLoadingTopPostsStats,
            loading: topPostsStatsLoading,
            page,
            pageSize,
            search,
            orderBy,
        }),
        [
            errorLoadingTopPostsStats,
            orderBy,
            page,
            pageSize,
            search,
            topPostsStats,
            topPostsStatsCount,
            topPostsStatsLoading,
        ]
    );

    const utils: CollectionTopPostsUtils = useMemo(
        () => ({
            refresh,
            setPage: (page: number) => setUrlState({ vP: page }),
            setPageSize: (pageSize: number | undefined) => setUrlState({ vPS: pageSize ?? 10 }),
            setSearch: (query: string) => setUrlState({ vS: query, vP: 1 }),
            setOrderBy: (orderBy: OrderByParam<TiktokAudioCollectionPostStats>) =>
                setUrlState({
                    vOD: orderBy.order_by_desc,
                    vOA: orderBy.order_by_asc,
                }),
        }),
        [refresh, setUrlState]
    );

    return [values, utils];
};

export default useCollectionTopPosts;
